/* ---- bb-engine.js ---- */
/**
 * Ski Exchange - binding bundles, compatibility engine
 * Peachy Digital Media, 29/07/2026
 *
 * The six gates from binding-bundle-framework.md section 5. Pure functions, no
 * DOM, no network, so the same file runs in the browser and under the tests.
 *
 * THE ONE RULE: this fails closed. Every gate that cannot be evaluated with
 * confirmed data removes the binding, or removes the whole module. A customer
 * seeing no picker is a missed upsell. A customer sold a binding that will not
 * mount to their ski is a safety problem and a refund. Never guess a match.
 */

/* global window */

(function (root) {
  'use strict';

  // A brake must clear the waist, and must not be so wide it drags in a turn.
  // Framework section 5 STEP 2.
  const BRAKE_TOLERANCE_MM = 15;

  // More than three options stops people choosing. Framework section 6.
  const MAX_SHOWN = 3;

  /* ---------- STEP 0: should the module render at all ---------- */

  function suppressionReason(ski) {
    if (!ski) return 'no ski';
    if (ski.exclude_from_bundles) return 'not a ski';
    if (ski.binding_included) return 'sold as a factory package, already has a binding';
    if (ski.binding_as_variant) return 'binding is still a variant option on this product';

    // The hard one. No waist width means the brake rule cannot run, and the
    // brake rule is the only thing standing between a customer and a binding
    // that physically will not close over their ski.
    if (!Number.isFinite(ski.waist_width)) return 'no confirmed waist width';

    // "Flat" and "System" are the only answers that mean anything. Anything
    // else is an unconfirmed row in Si's sheet.
    if (ski.mount_type !== 'Flat' && ski.mount_type !== 'System') return 'mount type not confirmed';
    if (ski.mount_type === 'System' && !ski.system_family) return 'system ski with no confirmed system family';

    return null;
  }

  /* ---------- STEP 1: mount gate ---------- */

  function mountCompatible(ski, binding) {
    if (ski.mount_type === 'System') {
      // A system ski takes its own rail and nothing else. Exact match only,
      // never a fuzzy one: "Tyrolia TP2" and "Tyrolia TP2 / LT rail" are not
      // the same string and must not be treated as interchangeable here.
      return binding.binding_type === 'System' && Boolean(binding.system_family) && binding.system_family === ski.system_family;
    }
    // A flat ski takes any drill mount, but never a rail binding.
    return binding.binding_type !== 'System';
  }

  /* ---------- STEP 2: brake width ---------- */

  function brakeFits(waistWidth, brakeMm) {
    if (!Number.isFinite(brakeMm)) return false;
    return brakeMm >= waistWidth && brakeMm <= waistWidth + BRAKE_TOLERANCE_MM;
  }

  /* ---------- STEP 3: discipline ---------- */

  function disciplineAllows(skiUse, bindingType) {
    // A rail binding is an alpine binding that happens to clip to a rail. It has
    // already passed the family match in STEP 1, so it must not then be dropped
    // here for not being called "Alpine".
    const type = bindingType === 'System' ? 'Alpine' : bindingType;

    if (skiUse === 'Touring') return type === 'Touring (pin/tech)' || type === 'Hybrid';
    if (skiUse === 'Freeride') return type === 'Alpine' || type === 'Hybrid';
    // Everything else is piste, all mountain or freestyle. A pin binding on a
    // piste ski is a mis-sell even though it would physically mount.
    return type === 'Alpine';
  }

  /* ---------- STEP 4: DIN range ---------- */

  // The gate runs only once we can actually compute the skier's setting, which
  // needs both the transcribed DIN table and a complete profile.
  //
  //   no indicative DIN     -> gate does not run, brake and mount gates still do
  //   indicative DIN known  -> the binding must have BOTH bounds, and the
  //                            setting must sit inside them
  //
  // The second half is deliberately strict. A Jester 16 is DIN 6 to 16, so a
  // skier whose setting is 4 cannot be wound down into it at all. If we know the
  // setting but not the binding's range, we do not know whether it fits, and
  // "do not know" resolves to no, like everywhere else in this engine.
  function dinAllows(binding, indicativeDin) {
    if (!Number.isFinite(indicativeDin)) return true;
    if (!Number.isFinite(binding.din_min) || !Number.isFinite(binding.din_max)) return false;
    return indicativeDin >= binding.din_min && indicativeDin <= binding.din_max;
  }

  /* ---------- STEP 5: exclusions ---------- */

  function variantSellable(variant, skiIsPreorder) {
    if (skiIsPreorder) return true;          // the ski is not in stock either
    return variant.available !== false;
  }

  /* ---------- the run ---------- */

  function compatibleBindings(ski, bindings, options) {
    const opts = options || {};
    const suppressed = suppressionReason(ski);
    if (suppressed) return { show: false, reason: suppressed, options: [] };

    const indicativeDin = Number.isFinite(opts.indicativeDin) ? opts.indicativeDin : null;
    const results = [];

    for (const binding of bindings || []) {
      if (binding.is_spare_part) continue;
      if (!binding.binding_type) continue;                        // unconfirmed type, fail closed
      if (!mountCompatible(ski, binding)) continue;
      if (!disciplineAllows(ski.ski_use, binding.binding_type)) continue;
      if (!dinAllows(binding, indicativeDin)) continue;

      for (const variant of binding.variants || []) {
        // On a system ski the rail fixes the brake, so the width rule does not
        // apply. On a flat ski it is the hard physical constraint.
        const fits = ski.mount_type === 'System' || brakeFits(ski.waist_width, variant.brake_width);
        if (!fits) continue;
        if (!variantSellable(variant, opts.skiIsPreorder)) {
          // Andrew confirmed on 27/07 that brake swaps are shop-wide, subject
          // to spare stock. So an out-of-stock width is surfaced with a caveat
          // rather than hidden, but it never outranks something we can post today.
          results.push({ binding, variant, in_stock: false, swap_note: true });
          continue;
        }
        results.push({ binding, variant, in_stock: true, swap_note: false });
      }
    }

    return { show: results.length > 0, reason: null, options: displayOrder(rank(results).slice(0, MAX_SHOWN)) };
  }

  /* ---------- STEP 6: rank ---------- */

  function rank(results) {
    // One entry per binding. If several brake widths fit, offer the narrowest
    // that clears the waist: it is the closest match and the least draggy.
    const best = new Map();
    for (const r of results) {
      const key = r.binding.id || r.binding.handle;
      const held = best.get(key);
      if (!held) { best.set(key, r); continue; }
      if (held.in_stock !== r.in_stock) { if (r.in_stock) best.set(key, r); continue; }
      if (r.variant.brake_width < held.variant.brake_width) best.set(key, r);
    }

    // SELECTION stays a DIN judgement: the slots go to the lightest fitting
    // bindings, which is what gives the list its lighter-to-stronger spread.
    // Ordering the slots by price is displayOrder()'s job, decided 14/08/2026:
    // selecting by price instead would hand every slot to the cheapest kit and
    // push the premium option off the page, which is not what Andy asked for.
    return [...best.values()].sort((a, b) => {
      if (a.in_stock !== b.in_stock) return a.in_stock ? -1 : 1;
      const am = Number.isFinite(a.binding.din_max) ? a.binding.din_max : Infinity;
      const bm = Number.isFinite(b.binding.din_max) ? b.binding.din_max : Infinity;
      if (am !== bm) return am - bm;
      return String(a.binding.title).localeCompare(String(b.binding.title));
    });
  }

  // DISPLAY is a price judgement, cheapest first (Andy via Luke, 14/08/2026).
  // Runs on the already-selected slots only. In stock still leads: a binding
  // that cannot post today never sits above one that can, whatever it costs.
  function displayOrder(options) {
    return [...options].sort((a, b) => {
      if (a.in_stock !== b.in_stock) return a.in_stock ? -1 : 1;
      const ap = Number.isFinite(a.variant.price) ? a.variant.price : Infinity;
      const bp = Number.isFinite(b.variant.price) ? b.variant.price : Infinity;
      if (ap !== bp) return ap - bp;
      return String(a.binding.title).localeCompare(String(b.binding.title));
    });
  }

  // The badge order (lighter to stronger) is a DIN statement, not a price
  // statement, so it is computed from DIN whatever order the list displays in.
  // Returns the option's rank among the shown set, for feeding into label().
  function dinRank(options, opt) {
    const byDin = [...options].sort((a, b) => {
      const am = Number.isFinite(a.binding.din_max) ? a.binding.din_max : Infinity;
      const bm = Number.isFinite(b.binding.din_max) ? b.binding.din_max : Infinity;
      if (am !== bm) return am - bm;
      return String(a.binding.title).localeCompare(String(b.binding.title));
    });
    return byDin.indexOf(opt);
  }

  /* ---------- tiers, for the UI copy ---------- */

  // Exactly one badge per position, and never the same word twice in one list.
  // The earlier version returned "Recommended" for both positions when only two
  // bindings fitted, which happens on any touring ski in the current range.
  function label(index, total) {
    if (total === 1) return 'Recommended';
    if (total === 2) return index === 0 ? 'Recommended' : 'Stronger';
    if (index === 0) return 'Lighter';
    if (index === 1) return 'Recommended';
    return 'Stronger';
  }

  const api = {
    BRAKE_TOLERANCE_MM,
    MAX_SHOWN,
    suppressionReason,
    mountCompatible,
    brakeFits,
    disciplineAllows,
    dinAllows,
    rank,
    displayOrder,
    label,
    dinRank,
    compatibleBindings,
  };

  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.BBEngine = api;
})(typeof window !== 'undefined' ? window : globalThis);

/* ---- din-chart.js ---- */
/**
 * Ski Exchange - binding bundles, indicative DIN lookup
 * Peachy Digital Media, 29/07/2026
 *
 * ISO 11088 release setting lookup. Weight and height give a skier code, skier
 * type and age adjust it, boot sole length picks the column.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE TABLE IS NOT IN THIS FILE AND MUST NOT BE WRITTEN FROM MEMORY.
 *
 * This file implements the lookup. The numbers live in din-table.json, which
 * ships empty. Transcribe them from the chart the workshop already uses, or
 * from the manufacturer's own published table, and record which one in the
 * `source` field.
 *
 * Two reasons, and the second is the one that matters:
 *
 *   1. Root CLAUDE.md: never guess a figure. A release setting is the most
 *      consequential figure in this project.
 *   2. Andrew and Si rejected the boot fit checker on 29/07 because it did not
 *      return what they would say. Shipping a second tool that disagrees with
 *      the fitters, on a number that decides whether a ski releases in a fall,
 *      would be considerably worse.
 *
 * Until the table is populated, indicativeDin() returns null. The engine treats
 * null as "gate does not run" and still shows compatible bindings, so the
 * picker works without it. The skier profile form must not ship until the table
 * is in and has been checked against ten real cases with Si.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/* global window */

(function (root) {
  'use strict';

  // Skier codes in ascending order of release setting. ISO 11088 runs A to O.
  const CODES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O'];

  // A cell that is genuinely blank on the printed chart is written "blank", not
  // null. They mean opposite things and must not share a representation: null
  // is "nobody has transcribed this yet" and has to stop the table loading,
  // "blank" is "the standard has no setting here" and is a valid, loadable
  // answer that sends the customer to a fitter.
  const BLANK = 'blank';

  let table = null;

  function load(json) {
    if (!json || !json.source || !json.weight_bands || !json.height_bands || !json.settings) {
      table = null;
      return { ok: false, error: 'din-table.json is missing required keys: source, weight_bands, height_bands, settings' };
    }
    const unfilled = Object.entries(json.settings)
      .filter(([, row]) => !Array.isArray(row) || row.some((v) => v === null || v === undefined));
    if (unfilled.length) {
      table = null;
      return {
        ok: false,
        error: `din-table.json has ${unfilled.length} rows still holding null (${unfilled.map(([c]) => c).join(', ')}). `
          + `Populate them from the workshop chart. Where the printed chart is blank, write "${BLANK}", not null.`,
      };
    }
    const wrongWidth = Object.entries(json.settings).filter(([, row]) => row.length !== json.bsl_columns.length);
    if (wrongWidth.length) {
      table = null;
      return { ok: false, error: `rows ${wrongWidth.map(([c]) => c).join(', ')} do not have one value per boot sole length column` };
    }
    table = json;
    return { ok: true, source: json.source };
  }

  const isLoaded = () => table !== null;

  /* ---------- code from weight and height ---------- */

  function bandCode(bands, value) {
    if (!Number.isFinite(value)) return null;
    for (const band of bands) {
      const overMin = band.min === null || value >= band.min;
      const underMax = band.max === null || value <= band.max;
      if (overMin && underMax) return band.code;
    }
    return null;
  }

  /**
   * ISO 11088 takes the LOWER of the weight code and the height code. A light
   * skier who is tall gets the lighter setting, not the taller one. Erring low
   * means an earlier release, which is the safe direction to be wrong in.
   */
  function baseCode(weightKg, heightCm) {
    const w = bandCode(table.weight_bands, weightKg);
    const h = bandCode(table.height_bands, heightCm);
    if (!w) return null;
    if (!h) return w;
    return CODES.indexOf(h) < CODES.indexOf(w) ? h : w;
  }

  /**
   * Skier type moves the code up. Type 1 cautious, 2 average, 3 aggressive.
   * Age moves it back down: under 10 and 50 or over both take a lower setting.
   * The adjustments are clamped, never wrapped.
   */
  function adjustedCode(code, skierType, ageYears) {
    let i = CODES.indexOf(code);
    if (i < 0) return null;

    if (skierType === 2) i += 1;
    else if (skierType === 3) i += 2;

    if (Number.isFinite(ageYears) && (ageYears < 10 || ageYears >= 50)) i -= 1;

    return CODES[Math.max(0, Math.min(CODES.length - 1, i))];
  }

  /* ---------- the lookup ---------- */

  function bslColumn(bootSoleLengthMm) {
    if (!Number.isFinite(bootSoleLengthMm)) return -1;
    return table.bsl_columns.findIndex(
      (c) => (c.min === null || bootSoleLengthMm >= c.min) && (c.max === null || bootSoleLengthMm <= c.max),
    );
  }

  /**
   * Returns { din, code, source } or { din: null, reason }.
   * Never throws, never guesses, never rounds a missing value into a real one.
   */
  function indicativeDin(profile) {
    if (!isLoaded()) return { din: null, reason: 'DIN table not loaded' };
    if (!profile) return { din: null, reason: 'no profile' };

    const { weight_kg: weight, height_cm: height, age_years: age, boot_sole_length_mm: bsl, skier_type: type } = profile;

    if (![1, 2, 3].includes(type)) return { din: null, reason: 'skier type must be 1, 2 or 3' };

    const base = baseCode(weight, height);
    if (!base) return { din: null, reason: 'weight is outside the chart' };

    const code = adjustedCode(base, type, age);
    const col = bslColumn(bsl);
    if (col < 0) return { din: null, reason: 'boot sole length is outside the chart' };

    const row = table.settings[code];
    if (!row) return { din: null, reason: `no row for skier code ${code}` };

    const din = row[col];
    // A blank cell in the printed chart means that combination is out of range
    // for the standard. It is not a zero and it is not the nearest neighbour.
    if (din === BLANK) {
      return { din: null, code, reason: `code ${code} with that boot sole length is blank on the chart, the fitter sets this one` };
    }
    if (!Number.isFinite(din)) {
      return { din: null, code, reason: `no setting for code ${code} at that boot sole length` };
    }

    return { din, code, source: table.source };
  }

  /**
   * Boot sole length sanity check. Does not block, it flags for the workshop to
   * phone. A customer reading the wrong number off their boot is the single
   * most likely way a shipped-mounted order goes wrong, and the mistake is
   * usually an order of magnitude, not a millimetre.
   */
  function bslLooksWrong(bootSoleLengthMm, mondoSize) {
    if (!Number.isFinite(bootSoleLengthMm)) return 'no boot sole length given';
    if (bootSoleLengthMm < 200 || bootSoleLengthMm > 400) return 'that is outside the range printed on any ski boot, check the number on the heel';
    if (Number.isFinite(mondoSize)) {
      // BSL runs roughly 60-70mm longer than the mondo point size. Well outside
      // that and the two do not describe the same boot.
      const expected = mondoSize * 10 + 65;
      if (Math.abs(bootSoleLengthMm - expected) > 25) return 'that does not match the boot size given, worth a call before mounting';
    }
    return null;
  }

  const api = { CODES, load, isLoaded, baseCode, adjustedCode, indicativeDin, bslLooksWrong };

  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.BBDin = api;
})(typeof window !== 'undefined' ? window : globalThis);

/* ---- bb-module.js ---- */
/**
 * Ski Exchange - binding bundles, product page controller
 * Peachy Digital Media, 29/07/2026
 *
 * Renders the binding picker on a flat ski, captures the skier profile when the
 * order is shipping mounted, and adds ski and binding to the basket as two real
 * line items in one action so stock comes off both.
 *
 * Runs inside an IIFE and touches only its own bb* ids, per the convention in
 * boot-fitting-service-page/PORT.md. Depends on bb-engine.js and din-chart.js.
 *
 * ON PRICING
 * The option list shows the binding's FULL price and says the discount comes off
 * in the basket. It deliberately does not render a pre-discounted figure. The
 * 10% is a native automatic discount Andrew can change himself in admin, and a
 * module that renders its own idea of the discounted price would start lying the
 * moment he did.
 */

/* global window, document, fetch, BBEngine, BBDin */

(function () {
  'use strict';

  const root = document.getElementById('bb-module');
  if (!root) return;

  const engine = window.BBEngine;
  const din = window.BBDin;
  if (!engine) return;

  /* ---------- config from Liquid ---------- */

  let ski;
  try {
    ski = JSON.parse(document.getElementById('bb-ski-data').textContent);
  } catch (e) {
    return;                                    // no data, render nothing
  }
  const settings = ski.settings || {};

  /* ---------- state ---------- */

  const state = {
    bindings: [],
    selected: null,              // { binding, variant } or null for "no binding"
    widthPick: {},               // binding product_id -> brake width mm, Andy 13/08
    colourPick: {},              // binding product_id -> variant id, Andy 13/08
    fitting: 'mounted',          // Andrew's default, 27/07
    skiVariantId: null,
    busy: false,
  };

  // The engine picks one variant per binding, the narrowest brake that clears
  // the waist. That stays the recommendation, but Andy's ask (13/08) is that a
  // wider brake should be a CHOICE, not just theoretically allowed. So every
  // variant of a shown binding that passes the same brake gate is offerable,
  // and the customer picks width and colour within that set. The gate itself
  // is unchanged: nothing outside waist to waist+15mm is ever offered.
  const fittingVariants = (opt) => (opt.binding.variants || []).filter((x) =>
    (ski.mount_type === 'System' || engine.brakeFits(ski.waist_width, x.brake_width)) &&
    (x.available !== false || String(x.id) === String(opt.variant.id)));

  const pickedWidth = (opt) => {
    const pick = state.widthPick[opt.binding.product_id];
    const widths = fittingVariants(opt).map((x) => x.brake_width);
    return Number.isFinite(pick) && widths.includes(pick) ? pick : opt.variant.brake_width;
  };

  const colourChoices = (opt) => {
    const w = pickedWidth(opt);
    const atWidth = fittingVariants(opt).filter((x) => x.brake_width === w && x.colour);
    return [...new Map(atWidth.map((x) => [x.colour, x])).values()];
  };

  /* ---------- colour swatches ---------- */

  // Indicative chips next to the colour dropdown, Andy's ask 13/08. The names
  // come from variant option values, so this maps the catalogue's actual
  // vocabulary and FAILS CLOSED: a name with any segment not listed here gets
  // no swatch at all, never a guessed colour. Multi-part names like Black/Tan
  // render as equal bands. Add new season names here as they appear.
  const SWATCH = {
    'black': '#111114', 'white': '#ffffff', 'grey': '#97999b', 'silver': '#c7c9cc',
    'sand': '#d6c39a', 'tan': '#c19a6b', 'teal': '#0f8a8a', 'yellow': '#ffd500',
    'neon yellow': '#e6ff2f', 'purple': '#7a4bb5', 'powder blue': '#a7c7e7',
    'lilac': '#c8a2c8', 'sachet pink': '#f2a7bb', 'iceberg green': '#a8dcc9',
    'flame orange': '#ff5a1e', 'grenadine': '#ce2f47', 'iced aqua': '#bfe6e2',
    'fusion coral': '#ff6f61', 'dune': '#cdb692', 'black metal': '#2e2e33',
    'orange metal': '#d96a2b', 'blue steel': '#4a6b8a', 'purple gold': '#7a5c9e',
    'icicle metallic': '#dde6ec', 'stargazer metallic': '#46527a',
    'trooper metallic': '#6b705c',
  };

  const swatchCss = (name) => {
    if (!name) return null;
    const parts = String(name).split('/').map((p) => SWATCH[p.trim().toLowerCase()]);
    if (parts.some((p) => !p)) return null;
    if (parts.length === 1) return parts[0];
    const band = 100 / parts.length;
    const stops = parts.map((c, i) => `${c} ${i * band}% ${(i + 1) * band}%`).join(', ');
    return `linear-gradient(90deg, ${stops})`;
  };

  const updateSwatch = (row, colourName) => {
    const chip = row.querySelector('.bb-swatch');
    if (!chip) return;
    const css = swatchCss(colourName);
    chip.hidden = !css;
    if (css) chip.style.background = css;
  };

  const resolvedVariant = (sel) => {
    if (!sel) return null;
    const w = pickedWidth(sel);
    const atWidth = fittingVariants(sel).filter((x) => x.brake_width === w);
    if (!atWidth.length) return sel.variant;
    const pick = state.colourPick[sel.binding.product_id];
    return atWidth.find((x) => String(x.id) === String(pick)) || atWidth[0];
  };

  /* ---------- helpers ---------- */

  const $ = (id) => document.getElementById(id);
  const money = (pence) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(pence, settings.money_format);
    }
    return `£${(pence / 100).toFixed(2).replace(/\.00$/, '')}`;
  };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));

  const skiVariant = () => (ski.variants || []).find((v) => String(v.id) === String(state.skiVariantId)) || null;

  /* ---------- watch the theme's variant selector ---------- */

  // Every Shopify product form carries the current variant in input[name="id"].
  // Reading that is theme-agnostic, where hooking Enterprise's own variant
  // events would not survive a theme update.
  const form = document.querySelector('form[action*="/cart/add"]');
  const variantInput = form ? form.querySelector('[name="id"]') : null;

  function readSkiVariant() {
    const next = variantInput ? variantInput.value : null;
    if (next === state.skiVariantId) return;
    state.skiVariantId = next;
    renderLock();
    renderTotals();
  }

  if (form) form.addEventListener('change', readSkiVariant);
  // Themes commonly set input.value from JS, which fires no event and mutates no
  // attribute, so neither a change listener nor a MutationObserver sees it. A
  // cheap poll is the only thing that reliably catches every theme.
  if (variantInput) window.setInterval(readSkiVariant, 500);

  /* ---------- render: the lock ---------- */

  function lengthChosen() {
    if (!variantInput) return true;               // no form to read, do not block
    if (!state.skiVariantId) return false;
    // A single-length ski is always "chosen".
    if ((ski.variants || []).length <= 1) return true;
    return Boolean(skiVariant());
  }

  function renderLock() {
    const locked = !lengthChosen();
    $('bb-locked-note').hidden = !locked;
    $('bb-body').classList.toggle('bb-locked', locked);
  }

  /* ---------- render: options ---------- */

  function render() {
    const result = engine.compatibleBindings(ski, state.bindings, {
      skiIsPreorder: Boolean(ski.preorder),
      indicativeDin: currentDin().din,
    });

    const list = $('bb-options');

    if (!result.show) {
      // Fails closed. If there is nothing we are certain fits, say nothing at
      // all rather than showing an empty picker or a maybe.
      root.hidden = true;
      return;
    }

    root.hidden = false;
    const total = result.options.length;

    list.innerHTML = [
      `<li><label class="bb-option is-selected" data-value="none">
         <input type="radio" name="bb-binding" value="none" checked>
         <span class="bb-option-head">
           <span class="bb-option-name">No binding, flat ski</span>
           <span class="bb-option-price">£0</span>
         </span>
       </label></li>`,
      ...result.options.map((opt, i) => {
        const b = opt.binding;
        const v = opt.variant;
        const widths = [...new Set(fittingVariants(opt).map((x) => x.brake_width))].sort((p, q) => p - q);
        const specs = [
          Number.isFinite(b.din_min) && Number.isFinite(b.din_max) ? `DIN ${b.din_min}-${b.din_max}` : null,
          // With a width dropdown the select is the source of truth for the
          // brake; a fixed number alongside it would contradict the choice.
          widths.length > 1 ? null : (Number.isFinite(v.brake_width) ? `${v.brake_width}mm brake` : null),
          (b.boot_norms || []).includes('GripWalk') ? 'GripWalk ready' : null,
        ].filter(Boolean).join(' · ');

        // Width and colour dropdowns render only where a real choice exists.
        // Width offers every brake width of this binding that passes the gate,
        // with the engine's narrowest-fit pick labelled as recommended. Colour
        // lists the colourways at the currently chosen width. Most bindings
        // have one width and one colour, so most options carry no dropdowns
        // and the list stays as quiet as before.
        const widthRow = widths.length > 1
          ? `<span class="bb-pick-row">Brake width
               <select class="bb-width" data-binding="${esc(b.product_id)}">
                 ${widths.map((w) => `<option value="${esc(w)}"${w === v.brake_width ? ' selected' : ''}>${esc(w)}mm${w === v.brake_width ? ', best fit' : ''}</option>`).join('')}
               </select>
             </span>`
          : '';
        // The colour row exists if ANY fitting width has a colour choice, and
        // hides when the currently picked width does not, so switching width
        // can reveal it without rebuilding the list and losing the selection.
        const coloursAt = (w) => {
          const at = fittingVariants(opt).filter((x) => x.brake_width === w && x.colour);
          return [...new Map(at.map((x) => [x.colour, x])).values()];
        };
        const colours = colourChoices(opt);
        const anyColourChoice = widths.some((w) => coloursAt(w).length > 1);
        const initialColour = (colours.find((x) => String(x.id) === String(v.id)) || colours[0] || {}).colour;
        const initialSwatch = swatchCss(initialColour);
        const colourRow = anyColourChoice
          ? `<span class="bb-pick-row"${colours.length > 1 ? '' : ' style="display:none"'}>Colour
               <span class="bb-swatch" aria-hidden="true"${initialSwatch ? ` style="background:${esc(initialSwatch)}"` : ' hidden'}></span>
               <select class="bb-colour" data-binding="${esc(b.product_id)}">
                 ${colours.map((x) => `<option value="${esc(x.id)}"${String(x.id) === String(v.id) ? ' selected' : ''}>${esc(x.colour)}</option>`).join('')}
               </select>
             </span>`
          : '';
        // Width and colour sit side by side and wrap on narrow screens.
        const picks = (widthRow || colourRow) ? `<span class="bb-picks">${widthRow}${colourRow}</span>` : '';

        return `<li><label class="bb-option" data-value="${esc(v.id)}">
          <input type="radio" name="bb-binding" value="${esc(v.id)}">
          <span class="bb-option-head">
            <span class="bb-option-name">${esc(b.vendor)} ${esc(b.title)}</span>
            <span class="bb-tier">${esc(engine.label(engine.dinRank(result.options, opt), total))}</span>
            <span class="bb-option-price">+${money(v.price)}</span>
          </span>
          <span class="bb-option-specs">${esc(specs)}</span>
          ${picks}
          ${opt.swap_note ? `<span class="bb-swap-note">${esc(settings.swap_note)}</span>` : ''}
        </label></li>`;
      }),
    ].join('');

    // Phrased to avoid the article entirely. "a 88mm" and "an 88mm" are both
    // wrong for some widths and the number is injected at render time, so there
    // is no article that reads correctly for 80, 88, 100 and 110 alike.
    $('bb-why').textContent = settings.why_short
      ? settings.why_short.replace('{waist}', ski.waist_width)
      : `Only bindings that fit this ski's ${ski.waist_width}mm waist are shown.`;

    list.querySelectorAll('input[name="bb-binding"]').forEach((input) => {
      input.addEventListener('change', () => onSelect(input.value, result.options));
    });

    list.querySelectorAll('.bb-colour').forEach((sel) => {
      sel.addEventListener('change', () => {
        state.colourPick[sel.dataset.binding] = sel.value;
        updateSwatch(sel.closest('.bb-pick-row'), sel.options[sel.selectedIndex].text);
        renderTotals();
      });
    });

    list.querySelectorAll('.bb-width').forEach((sel) => {
      sel.addEventListener('change', () => {
        const bid = sel.dataset.binding;
        state.widthPick[bid] = Number(sel.value);
        // A width change invalidates the colour pick: that variant may not
        // exist at the new width. The colour select is refreshed in place
        // rather than re-rendering the list, which would drop the selection.
        delete state.colourPick[bid];
        const opt = result.options.find((o) => String(o.binding.product_id) === String(bid));
        const row = sel.closest('.bb-option');
        const colourSel = row ? row.querySelector('.bb-colour') : null;
        if (opt && colourSel) {
          const colours = colourChoices(opt);
          colourSel.innerHTML = colours
            .map((x) => `<option value="${esc(x.id)}">${esc(x.colour)}</option>`)
            .join('');
          colourSel.parentElement.style.display = colours.length > 1 ? '' : 'none';
          updateSwatch(colourSel.closest('.bb-pick-row'), (colours[0] || {}).colour);
        }
        renderTotals();
      });
    });
  }

  function onSelect(value, options) {
    document.querySelectorAll('.bb-option').forEach((el) => {
      el.classList.toggle('is-selected', el.dataset.value === value);
    });

    state.selected = value === 'none'
      ? null
      : options.find((o) => String(o.variant.id) === String(value)) || null;

    const picked = Boolean(state.selected);
    // The profile block only appears once a binding is chosen. Nobody should be
    // asked their weight to buy a flat ski.
    $('bb-fitting').hidden = !picked;
    $('bb-submit').hidden = !picked;
    renderProfileVisibility();
    renderTotals();
    renderDin();
  }

  /* ---------- render: fitting and profile ---------- */

  function renderProfileVisibility() {
    // Collect in store means a fitter takes the measurements in person, so the
    // form is not required. Shipping mounted means nobody ever sees the skier.
    $('bb-profile').hidden = state.fitting !== 'mounted';
  }

  document.querySelectorAll('input[name="bb-fitting"]').forEach((input) => {
    input.addEventListener('change', () => {
      state.fitting = input.value;
      document.querySelectorAll('.bb-choice-option').forEach((el) => {
        el.classList.toggle('is-selected', el.querySelector('input').checked);
      });
      renderProfileVisibility();
      renderDin();
    });
  });

  /* ---------- the indicative setting ---------- */

  function profileValues() {
    const num = (id) => {
      const el = $(id);
      const v = el && el.value !== '' ? Number(el.value) : NaN;
      return Number.isFinite(v) ? v : null;
    };
    return {
      weight_kg: num('bb-weight'),
      height_cm: num('bb-height'),
      age_years: num('bb-age'),
      boot_sole_length_mm: num('bb-bsl'),
      skier_type: num('bb-type'),
    };
  }

  function currentDin() {
    if (!din || !din.isLoaded() || state.fitting !== 'mounted') return { din: null };
    const p = profileValues();
    if (Object.values(p).some((v) => v === null)) return { din: null };
    return din.indicativeDin(p);
  }

  function renderDin() {
    const box = $('bb-din');
    const result = currentDin();

    if (!result.din) { box.hidden = true; return; }
    box.hidden = false;
    $('bb-din-value').textContent = `Indicative setting: DIN ${result.din}`;
  }

  ['bb-weight', 'bb-height', 'bb-age', 'bb-bsl', 'bb-type'].forEach((id) => {
    const el = $(id);
    if (el) el.addEventListener('input', () => { validateField(id); renderDin(); render(); });
  });

  /* ---------- validation ---------- */

  function validateField(id) {
    const el = $(id);
    if (!el) return true;
    const wrap = el.closest('.bb-field');
    const errEl = wrap ? wrap.querySelector('.bb-error') : null;
    let message = '';

    if (el.value === '') {
      message = '';                              // empty is handled at submit
    } else if (id === 'bb-bsl') {
      // Flagged, never blocked. A wrong sole length is the most likely way a
      // shipped-mounted order goes wrong, and the mistake is usually an order of
      // magnitude rather than a millimetre.
      const warn = din ? din.bslLooksWrong(Number(el.value)) : null;
      message = warn || '';
    }

    if (wrap) wrap.classList.toggle('has-error', Boolean(message));
    if (errEl) errEl.textContent = message;
    return true;
  }

  function missingFields() {
    if (state.fitting !== 'mounted') return [];
    return Object.entries(profileValues()).filter(([, v]) => v === null).map(([k]) => k);
  }

  /* ---------- totals ---------- */

  function renderTotals() {
    const v = skiVariant();
    const line = $('bb-total');
    if (!v || !state.selected) { line.textContent = ''; return; }
    const bv = resolvedVariant(state.selected);
    line.textContent = `${money(v.price)} + ${money(bv.price)}${settings.discount_note ? `, ${settings.discount_note}` : ''}`;
  }

  /* ---------- add to cart ---------- */

  function say(message, stateName) {
    const el = $('bb-status');
    el.textContent = message;
    el.dataset.state = stateName || '';
  }

  function bundleId() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return `bb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  $('bb-submit').addEventListener('click', async () => {
    if (state.busy) return;

    const v = skiVariant();
    if (!v) { say('Choose a length first.', 'error'); return; }
    if (!state.selected) return;

    const missing = missingFields();
    if (missing.length) {
      say('We need all five to set your bindings. Fill in the boxes above, or choose to collect in store and our fitter will take them.', 'error');
      $(missing[0]).focus();
      return;
    }

    const p = profileValues();
    const id = bundleId();
    const fittingLabel = state.fitting === 'mounted'
      ? 'Delivered, mounted and set up'
      : 'Collect from Dry Drayton, near Cambridge, fitted in store';

    // Visible properties are the ones the customer should be able to check on
    // their own order. Underscore-prefixed ones are hidden from the customer and
    // visible to the workshop in admin.
    const skiProps = {
      Fitting: fittingLabel,
      _bundle_id: id,
      _bundle_role: 'ski',
      _mount_type: ski.mount_type,
    };
    const bindingVariant = resolvedVariant(state.selected);
    const bindingProps = {
      Fitting: fittingLabel,
      _bundle_id: id,
      _bundle_role: 'binding',
      _mount_type: ski.mount_type,
      _brake_width: String(bindingVariant.brake_width),
      _mount_to_ski: ski.title,
    };
    // Visible so the customer can check the colour they picked on their order.
    if (bindingVariant.colour) bindingProps.Colour = bindingVariant.colour;

    if (state.fitting === 'mounted') {
      const result = currentDin();
      skiProps['Boot sole length'] = `${p.boot_sole_length_mm}mm`;
      skiProps._skier_profile = `weight ${p.weight_kg}kg, height ${p.height_cm}cm, age ${p.age_years}, type ${p.skier_type}`;
      // Only ever recorded when the chart actually produced one. A blank here
      // means the workshop sets it, and that is a safer order note than a number
      // nobody can trace to a chart.
      if (result.din) {
        skiProps.Setting = `DIN ${result.din} (indicative)`;
        bindingProps._din_indicative = String(result.din);
      }
    }

    state.busy = true;
    $('bb-submit').disabled = true;
    say('Adding to your basket...');

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            { id: Number(v.id), quantity: 1, properties: skiProps },
            { id: Number(bindingVariant.id), quantity: 1, properties: bindingProps },
          ],
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        // Shopify rejects the whole call if either line is short, which is the
        // behaviour we want: never a ski in the basket with no binding to mount.
        throw new Error(body.description || body.message || 'That did not go through.');
      }

      say('Added. Skis and binding are in your basket.');
      document.dispatchEvent(new CustomEvent('bb:added', { detail: { bundleId: id } }));
      window.location.href = '/cart';
    } catch (err) {
      say(`${err.message} Give us a call on ${settings.phone || '01954 210984'} and we will sort it.`, 'error');
      state.busy = false;
      $('bb-submit').disabled = false;
    }
  });

  /* ---------- boot ---------- */

  (async function init() {
    readSkiVariant();
    renderLock();

    // The DIN table is optional. Without it the picker still works and the
    // profile block simply shows no indicative setting.
    if (din && settings.din_table_url) {
      try {
        const r = await fetch(settings.din_table_url);
        if (r.ok) din.load(await r.json());
      } catch (e) { /* picker works without it */ }
    }

    try {
      const r = await fetch(settings.bindings_url);
      if (!r.ok) throw new Error('bindings dataset unavailable');
      const data = await r.json();
      state.bindings = data.bindings || [];
    } catch (e) {
      // No dataset means no confident matches, so show nothing at all.
      root.hidden = true;
      return;
    }

    render();
  })();
})();